/**
 * FreePlaySpendBanner — small inline card on the Settings tab that shows
 * how much free play has been given out in the last 7 + 30 days plus an
 * estimated trim from the active throttles. Helps the admin "see" their
 * dial moves in real $$ before/after.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingDown, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Tile = ({ label, value, sub, tone = 'neutral' }) => {
    const cls = tone === 'red' ? 'text-rose-300' : tone === 'green' ? 'text-emerald-300' : 'text-zinc-100';
    return (
        <div className="flex-1 min-w-[140px]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className={`text-lg font-mono font-bold mt-1 ${cls}`}>{value}</div>
            {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
        </div>
    );
};

const FreePlaySpendBanner = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const { data: resp } = await axios.get(`${API}/admin/freeplay-spend`, { withCredentials: true });
                if (mounted) { setData(resp); setLoading(false); }
            } catch (err) {
                console.error(err);
                if (mounted) setLoading(false);
            }
        };
        load();
        const id = setInterval(load, 30_000);
        return () => { mounted = false; clearInterval(id); };
    }, []);

    if (loading) {
        return (
            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4 text-zinc-500 text-xs flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading free-play spend…
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4" data-testid="freeplay-spend-banner">
            <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 text-amber-300" />
                <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                    Free play given out · live · all sources
                </span>
            </div>
            <div className="flex flex-wrap gap-6">
                <Tile label="Last 7 days" value={fmtMoney(data.last_7_days)}
                    sub={`${data.last_7_days_count} grants · avg ${fmtMoney(data.last_7_days_avg)}`}
                    tone="red" />
                <Tile label="Last 30 days" value={fmtMoney(data.last_30_days)}
                    sub={`${data.last_30_days_count} grants`} />
                <Tile label="Daily-bonus alone, 7d" value={fmtMoney(data.daily_bonus_7d)} tone="red" />
                <Tile label="Today" value={fmtMoney(data.today)}
                    sub="Central Time" tone="neutral" />
            </div>
            <p className="text-[11px] text-zinc-400 mt-3 leading-snug">
                Numbers refresh every 30 s. Dial your throttles above (rollover, max streak, weekly cap) and watch this drop over the next few days. <span className="text-amber-300">Target: drop daily-bonus 7d by ~50%.</span>
            </p>
        </div>
    );
};

export default FreePlaySpendBanner;
